import { Link } from "react-router-dom";

/**
 * Co-branding officiel (rectification Kru Ralph — juin 2026) :
 * « INSTITUT IMPACT ÉLITE MARTIAL » × « KAIZEN SYSTEM FRANCHISE » TOUJOURS ENSEMBLE.
 * Identité : Premium · Internationale · Épurée · Élite · Intemporelle.
 * Charte : /app/design_guidelines.json (theme luxury + swiss high-contrast).
 *  - Serif Cormorant Garamond → Institut Impact (les racines)
 *  - Sans Outfit → Kaizen System (l'évolution / la franchise)
 *
 * Variants:
 *  - default : lockup complet dans un <Link> (landing/nav, fond CLAIR)
 *  - static  : lockup complet sans <Link> (sidebar admin, fond SOMBRE)
 *  - compact : monogramme seul
 *
 * tone: "dark"  → texte sombre (pour fonds clairs)
 *       "light" → texte clair  (pour fonds sombres)
 *       (déduit du variant si non fourni)
 */
export default function Logo({ variant = "default", tone, className = "", product = false }) {
  const resolvedTone = tone || (variant === "static" ? "light" : "dark");

  if (variant === "compact") {
    return (
      <div className={`inline-flex items-center ${className}`}>
        <Monogram tone={resolvedTone} />
      </div>
    );
  }

  const lockup = (
    <>
      {product
        ? <img src="/brand/dojova-icon-192.png" alt="DOJOVA" width={variant === "static" ? 34 : 44} height={variant === "static" ? 34 : 44} className={`${variant === "static" ? "w-[34px] h-[34px]" : "w-11 h-11"} rounded-xl shrink-0`} data-testid="dojova-mark" />
        : <MasterMark tone={resolvedTone} compact={variant === "static"} />}
      {product ? <ProductLockup tone={resolvedTone} compact={variant === "static"} /> : <BrandLockup tone={resolvedTone} compact={variant === "static"} />}
    </>
  );

  if (variant === "static") {
    return (
      <div className={`inline-flex items-center gap-2.5 group min-w-0 ${className}`} data-testid="header-cobrand-logo">
        {lockup}
      </div>
    );
  }

  return (
    <Link to="/" className={`inline-flex items-center gap-3 sm:gap-4 group ${className}`} data-testid="header-cobrand-logo">
      {lockup}
    </Link>
  );
}

/** Bloc produit : DOJOVA — Kaizen System (le logiciel ; l'école reste Institut Impact Élite Martial). */
function ProductLockup({ tone, compact = false }) {
  const main = tone === "light" ? "text-[#F8F5F2]" : "text-[#0A0A0A]";
  const sub = tone === "light" ? "text-zinc-400" : "text-zinc-500";
  return (
    <div className="flex items-center gap-2.5 sm:gap-3.5 min-w-0" data-testid="product-lockup">
      <div className="flex flex-col items-start leading-none min-w-0">
        <span className={`brand-sans font-bold tracking-[0.26em] uppercase ${compact ? "text-[11px]" : "text-sm sm:text-base"} ${main} whitespace-nowrap`}>Dojova</span>
        <span className={`brand-sans font-light tracking-[0.2em] uppercase ${compact ? "text-[7px] mt-[2px]" : "text-[8px] sm:text-[9px] mt-[3px]"} ${sub} whitespace-nowrap`}>— Kaizen System</span>
      </div>
    </div>
  );
}

/** Bloc typographique co-brandé. */
function BrandLockup({ tone, compact = false }) {
  const c = tone === "light"
    ? {
        institut: "text-[#F8F5F2]",
        elite: "text-[#C5A059]",
        kaizen: "text-[#F8F5F2]",
        franchise: "text-zinc-400",
        sep: "bg-white/20",
      }
    : {
        institut: "text-[#0A0A0A]",
        elite: "text-[#A68641]",
        kaizen: "text-[#0A0A0A]",
        franchise: "text-zinc-500",
        sep: "bg-black/15",
      };

  // === Disposition EMPILÉE (sidebar étroite) ===
  if (compact) {
    return (
      <div className="flex flex-col items-start leading-none min-w-0">
        <span className={`brand-sans font-medium tracking-[0.2em] uppercase text-[10px] ${c.kaizen} whitespace-nowrap`}>
          Kaizen System
        </span>
        <span className={`brand-sans font-light tracking-[0.22em] uppercase text-[7px] ${c.franchise} mt-[2px] whitespace-nowrap`}>
          Franchise
        </span>
      </div>
    );
  }

  // === Disposition HORIZONTALE (header large) ===
  return (
    <div className="flex items-center gap-2.5 sm:gap-3.5 min-w-0">
      <div className={`w-px h-8 sm:h-9 ${c.sep} shrink-0`} aria-hidden="true" />
      <div className="flex flex-col items-start leading-none min-w-0">
        <span className={`brand-sans font-medium tracking-[0.22em] uppercase text-xs sm:text-sm ${c.kaizen} whitespace-nowrap`}>
          Kaizen System
        </span>
        <span className={`brand-sans font-light tracking-[0.24em] uppercase text-[8px] sm:text-[9px] ${c.franchise} mt-[3px] whitespace-nowrap`}>
          Franchise
        </span>
      </div>
    </div>
  );
}

/** Logo maître officiel Institut Impact Élite Martial (bloc texte, fond transparent). */
function MasterMark({ tone, compact = false }) {
  const img = (
    <img
      src="/logos/master_text_sm.webp"
      alt="Institut Impact Élite Martial"
      className={`${compact ? "h-12" : "h-10 sm:h-12"} w-auto object-contain shrink-0 transition-transform duration-300 group-hover:scale-[1.03]`}
      data-testid="master-logo-img"
    />
  );
  if (tone === "dark") {
    return <span className="inline-flex items-center bg-[#0a0a0a] rounded-md px-2 py-1 shrink-0">{img}</span>;
  }
  return img;
}

/**
 * Monogramme premium : enso (cercle brossé) en rouge martial intersecté d'un « K »
 * architectural, touche d'or. Vectoriel pour rester net à toutes tailles.
 */
function Monogram({ tone = "dark", size = 42 }) {
  const kColor = tone === "light" ? "#F8F5F2" : "#0A0A0A";
  return (
    <div
      className="relative flex items-center justify-center select-none shrink-0 transition-transform duration-300 group-hover:scale-105"
      style={{ width: size, height: size }}
    >
      <svg viewBox="0 0 48 48" className="absolute inset-0 w-full h-full" style={{ filter: "drop-shadow(0 0 5px rgba(217,4,41,0.28))" }}>
        {/* Enso — trait principal rouge martial, lisse et effilé */}
        <circle
          cx="24" cy="24" r="18"
          fill="none"
          stroke="#D90429"
          strokeWidth="3.2"
          strokeLinecap="round"
          strokeDasharray="100 13"
          transform="rotate(-28 24 24)"
        />
        {/* Touche d'or premium en fin de trait */}
        <circle
          cx="24" cy="24" r="18"
          fill="none"
          stroke="#C5A059"
          strokeWidth="3.2"
          strokeLinecap="round"
          strokeDasharray="9 104"
          transform="rotate(60 24 24)"
        />
      </svg>
      {/* K architectural */}
      <span
        className="brand-sans font-semibold leading-none relative z-10"
        style={{ color: kColor, fontSize: size * 0.46 }}
      >
        K
      </span>
    </div>
  );
}
